import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { Backdrop, CircularProgress } from "@mui/material";
import { format } from "date-fns";
import { isEmpty, isEqual, isNil } from "lodash";
import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import Box from "@mui/material/Box"
import Autocomplete from "@mui/material/Autocomplete"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography";
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

import CustomHeaderGroup from "@plannel/components/aggrid/CustomHeaderGroup";
import DataState from "@plannel/components/aggrid/DataState"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import { ResultButton, RunButton, CheckButton } from "@plannel/components/ActionIconButton";

import targetInvEvalService from "@plannel/services/ip/target-inv-eval-service";
import planConfigService from "@plannel/services/data/plan-config-service";

import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import ResponseUtil from "@plannel/pages/utils/response-util";
import { DateField, DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import appConfigService from "@plannel/services/system/app-config-service";
import numberUtil from "@plannel/utils/number-util";
import { FS } from "@plannel/utils/font-util";

const INV_CLASS_CODE = "invClassCd";
const CATEGORY_NAME = "categoryName";
const LOCATION_NAME = "locName";
const DEMAND = "DEMAND";
const ACTUAL = "ACTUAL";
const ACTUAL_PERIOD_VAL = "ACTUAL_PERIOD_VAL";
const DEMAND_PERIOD_VAL = "DEMAND_PERIOD_VAL";
const ACTUAL_PERIOD_GAP = "ACTUAL_PERIOD_GAP";
const DEMAND_PERIOD_GAP = "DEMAND_PERIOD_GAP";

const RUN_EVALUATION = "RUN_EVALUATION";
const APPLY_EVALUATION = "APPLY_EVALUATION";
const EVALUATION_DETAIL = "EVALUATION_DETAIL";
const { httpStatusCode } = ResponseUtil;

const CURRENT_DATE = new ZDate();
const ACTUAL_MIN_DATE = new ZDate(CURRENT_DATE.getFullYear() - 5, CURRENT_DATE.getMonth(), 1);
const ACTUAL_MAX_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 0);
const DEMAND_MIN_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 1);
const DEMAND_MAX_DATE = new ZDate(CURRENT_DATE.getFullYear() + 5, CURRENT_DATE.getMonth(), 0);
const { snackBarMessageColor } = ColorUtil;
const TargetInventoryEvaluation = ({ t, title, viewName }) => {
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();

  const PERIOD_DATA_ARRAY = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
  const [actualMonths, setActualMonths] = useState(PERIOD_DATA_ARRAY[3]);
  const [demandMonths, setDemandMonths] = useState(PERIOD_DATA_ARRAY[3]);
  const [actualStartDate, setActualStartDate] = useState(new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() - Number(PERIOD_DATA_ARRAY[3]), 1));
  const [actualEndDate, setActualEndDate] = useState(new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 0));
  const [demandStartDate, setDemandStartDate] = useState(new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 1));
  const [demandEndDate, setDemandEndDate] = useState(new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + Number(PERIOD_DATA_ARRAY[3]), 0));
  const [progressOn, setProgressOn] = useState(false);

  const [categoryKeyValue, setCategoryKeyValue] = useState([]);
  const [locationKeyValue, setLocationKeyValue] = useState([]);
  const [ispEvaluation, setIspEvaluation] = useState([]);
  const [ispEvaluationTotal, setIspEvaluationTotal] = useState({ invClassCd: "Total" });
  const [deployedStrategy, setDeployedStrategy] = useState({});

  const [gridApi, setGridApi] = useState(null);
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const gridRef = useRef(null);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setEvaluationMonths = () => {
    planConfigService.getConfigCd({ moduleCd: CodeUtil.module.IP, configCd: EVALUATION_DETAIL })
      .then(response => {
        const { objVal } = response.data;

        if (!isEmpty(objVal)) {
          const actualMonths = isNil(objVal[ACTUAL_PERIOD_VAL]) ? 0 : objVal[ACTUAL_PERIOD_VAL];
          const demandMonths = isNil(objVal[DEMAND_PERIOD_VAL]) ? 0 : objVal[DEMAND_PERIOD_VAL];
          const actualPeriodGap = isNil(objVal[ACTUAL_PERIOD_GAP]) ? 0 : objVal[ACTUAL_PERIOD_GAP];
          const demandPeriodGap = isNil(objVal[DEMAND_PERIOD_GAP]) ? 0 : objVal[DEMAND_PERIOD_GAP];

          const aEndDate = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() - Number(actualPeriodGap), 0);
          const dStartDate = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + Number(demandPeriodGap), 1);

          if (actualMonths === 0) {
            setActualStartDate(null);
          } else {
            setActualStartDate(new ZDate(aEndDate.getFullYear(), aEndDate.getMonth() + 1 - Number(actualMonths), 1));
            setActualEndDate(aEndDate);
          }
          setActualMonths(String(actualMonths));

          if (demandMonths === 0) {
            setDemandEndDate(null);
          } else {
            setDemandStartDate(dStartDate);
            setDemandEndDate(new ZDate(dStartDate.getFullYear(), dStartDate.getMonth() + Number(demandMonths), 0));
          }

          setDemandMonths(String(demandMonths));
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveIpEvaluation = () => {
    targetInvEvalService.getOverview()
      .then(overviewResponse => {
        const { results, categoryMap, locationMap } = overviewResponse.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setCategoryKeyValue(categoryMap);
        setLocationKeyValue(locationMap);
        setIspEvaluation(results);
      })
      .then(() => {
        DataState.initialize(gridApi);
      })

    targetInvEvalService.getDeployedStrategy()
      .then(response => {
        setDeployedStrategy(response.data);
      });
  }

  const calculateTotal = () => {
    setIspEvaluationTotal([]);

    const totalRow = {
      invClassCd: "Total",
      skus: null,
      targetInvQty: null,
      targetInvAmt: null,
      newTargetInvQty: null,
      newTargetInvAmt: null,
      currentInvQty: null,
      currentInvQtyGap: null,
      currentInvQtyGapRate: null,
      currentInvAmt: null,
      currentInvAmtGap: null,
      currentInvAmtGapRate: null,
      avgInvQty: null,
      avgInvAmt: null,
      currentExcess: null,
      currentShortage: null,
      currentExist: null,
      riskStockout: null,
      riskExcess: null,
      riskObsolescence: null,
      trendRising: null,
      trendShirinking: null
    };

    for (const row of ispEvaluation) {
      Object.entries(totalRow).forEach((el) => {
        const field = el[0];
        if (field !== INV_CLASS_CODE) {
          totalRow[field] = totalRow[field] + row[field];
        }
      });
    }

    totalRow.currentInvQtyGapRate = totalRow.targetInvQty > 0 ? totalRow.currentInvQtyGap / totalRow.targetInvQty * 100 : 0;
    totalRow.currentInvAmtGapRate = totalRow.targetInvAmt > 0 ? totalRow.currentInvAmtGap / totalRow.targetInvAmt * 100 : 0;

    setIspEvaluationTotal(totalRow);
  }

  const getEvaluationParams = () => {
    return {
      actualEvalEndDate: actualEndDate,
      actualEvalPeriodVal: actualMonths,
      demandEvalStartDate: demandStartDate,
      demandEvalPeriodVal: demandMonths,
      nowDate: CURRENT_DATE,
    }
  }

  const runCurrentStrategyEval = () => {
    setProgressOn(true);

    const params = getEvaluationParams();
    targetInvEvalService.runEvaluation(params)
      .then(response => {
        if (response.status === httpStatusCode.Ok) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_StrategyEvalSuccess") });
          retrieveIpEvaluation();
        } else if (response.status === httpStatusCode.NoContent) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_NoAppliedScenario") });
          setDeployedStrategy({});
          setIspEvaluation([]);
        }
      })
      .catch(e => {
        const { response } = e;
        if (response.status === httpStatusCode.BadRequest) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t(response.data) });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_StrategyEvalFailed") });
        }

        debug(e);
      })
      .finally(() => {
        setProgressOn(false);
      })
  }

  const applyEvaluation = () => {
    setProgressOn(true);
    targetInvEvalService.applyEvaluation()
      .then(response => {
        if (response.status === 200) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_StrategyEvalApplySuccess") });
          retrieveIpEvaluation();
        } else if (response.status === 204) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_StrategyEvalApplyFailed") });
          setDeployedStrategy({});
          setIspEvaluation([]);
        }
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_StrategyEvalApplyFailed") });
        debug(e);
      })
      .finally(() => {
        setProgressOn(false);
      })
  }

  const handleDialogHandler = async (action) => {
    const dialogInfo = { open: true, actionName: action, title: "", content: "" };
    if (action === RUN_EVALUATION) {
      const monthlyPSICheckMessage = await appConfigService.asyncCheckMonthlyPSIData();
      dialogInfo.title = "titleRunEvaluation";
      dialogInfo.content = t("MSG_RunEvalConfirm");

      if (monthlyPSICheckMessage === "MSG_MonthlyTransformUpdateRequired") {
        dialogInfo.content = `${t("MSG_RunEvalConfirm")}\n${t(monthlyPSICheckMessage)}`;
      } else if (monthlyPSICheckMessage === "MSG_MonthlyTransformRequired") {
        dialogInfo.actionName = "";
        dialogInfo.title = "info";
        dialogInfo.content = t(monthlyPSICheckMessage);
      }
    } else if (action === APPLY_EVALUATION) {
      dialogInfo.title = "titleEvaluationApply";
      dialogInfo.content = t("MSG_ApplyStrategyEval");
    }

    setDialogInfo(dialogInfo);
  }

  const handleDialog = (action) => {
    if (action === RUN_EVALUATION) {
      runCurrentStrategyEval();
    } else if (action === APPLY_EVALUATION) {
      applyEvaluation();
    }
  }

  const onChangeEvalMonths = (newValue, target) => {
    if (target === ACTUAL) {
      setActualMonths(newValue);

      setActualStartDate(new ZDate(actualEndDate.getFullYear(), actualEndDate.getMonth() - Number(newValue) + 1, 1));
    } else {
      setDemandMonths(newValue);

      setDemandEndDate(new ZDate(demandStartDate.getFullYear(), demandStartDate.getMonth() + Number(newValue), 0));
    }

  }

  // 평가 결과 화면이 권한 밖이면 결과 버튼도 셀 링크도 내보내지 않는다.
  const canOpenEvalResult = isPermitted("TARGET_INV_EVAL_RESULT");

  const cellClicked = (filter, location) => {
    const reduxKey = "TARGET_INV_EVAL_RESULT";
    const cellClickedReduxState = reduxUtil.createObjectState(reduxKey, { riskCd: null, itemSearch: null, currentPage: 1, pageSize: 100, filter, location });

    reduxDispatch(reduxUtil.updateViewState(cellClickedReduxState));
    reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
  }

  const groupCellRenderer = (params, field) => {
    let location = {};
    let category = {};
    const { value } = params;
    let parentKey = params.node?.parent;
    if (parentKey.field === INV_CLASS_CODE) {
      if (parentKey?.parent) {
        parentKey = parentKey.parent?.key;
      } else {
        parentKey = null;
      }
    } else {
      parentKey = parentKey.key;
    }

    if (field === CATEGORY_NAME) {
      category.name = value;
      category.hrchyCd = categoryKeyValue[value];
      category.leafFlg = true;

      if (parentKey) {
        location.name = parentKey;
        location.locCd = locationKeyValue[parentKey];
      } else {
        location = null;
      }
    } else {
      location.name = value;
      location.locCd = locationKeyValue[value];

      if (parentKey) {
        category.name = parentKey;
        category.hrchyCd = categoryKeyValue[parentKey];
        category.leafFlg = true;
      } else {
        category = null;
      }
    }
    const filter = { category: [category] };

    return (
      canOpenEvalResult
        ? <div style={{ color: "blue", textDecoration: "underline", cursor: "pointer" }} onClick={() => cellClicked(filter, location)} role="button" tabIndex={0}>{value}</div>
        : <div>{value}</div>
    );
  }

  const showResultsPage = () => {
    const reduxKey = "TARGET_INV_EVAL_RESULT";

    reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
  }

  const columnDefs = [
    {
      headerName: CATEGORY_NAME,
      field: CATEGORY_NAME,
      width: 150,
      type: ["nonEditableColumn"],
      hide: true,
      rowGroup: true,
      enableRowGroup: true,
      cellRenderer: (params) => groupCellRenderer(params, CATEGORY_NAME)
    },
    {
      headerName: LOCATION_NAME,
      field: LOCATION_NAME,
      width: 150,
      type: ["nonEditableColumn"],
      rowGroup: true,
      hide: true,
      enableRowGroup: true,
      cellRenderer: (params) => groupCellRenderer(params, LOCATION_NAME)
    },
    {
      headerName: INV_CLASS_CODE,
      field: INV_CLASS_CODE,
      width: 70,
      type: ["nonEditableColumn"],
      hide: true,
      rowGroup: true,
      enableRowGroup: true,
    },
    {
      headerName: "skus",
      field: "skus",
      width: 80,
      type: ["numberColumn", "nonEditableColumn", "rightAligned"],
      pinned: 'left',
      aggFunc: 'sum',
      enableValue: true,
    },
    {
      headerName: "targetStock",
      children: [
        {
          headerName: "qty",
          field: "targetInvQty",
          width: 120,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          aggFunc: 'sum',
          enableValue: true,
        },
        {
          headerName: "amt",
          field: "targetInvAmt",
          width: 120,
          type: ["currencyNumberColumn", "nonEditableColumn", "rightAligned"],
          aggFunc: 'sum',
          enableValue: true,
        },
      ]
    },
    {
      headerName: "newTarget",
      children: [
        {
          headerName: "qty",
          field: "newTargetInvQty",
          width: 120,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          aggFunc: 'sum',
          enableValue: true,
        },
        {
          headerName: "amt",
          field: "newTargetInvAmt",
          width: 120,
          type: ["currencyNumberColumn", "nonEditableColumn", "rightAligned"],
          aggFunc: 'sum',
          enableValue: true,
        },
      ]
    },
    {
      headerName: "onhandStock",
      headerGroupComponent: CustomHeaderGroup,
      children: [
        {
          headerName: "onhandQty",
          field: "currentInvQty",
          width: 100,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          aggFunc: 'sum',
          enableValue: true,
        },
        {
          headerName: "qtyGapFromTarget",
          field: "currentInvQtyGap",
          width: 132,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          columnGroupShow: "open",
          aggFunc: 'sum',
          enableValue: true,
        },
        {
          headerName: "qtyGapPctFromTarget",
          field: "currentInvQtyGapRate",
          width: 132,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          columnGroupShow: "open",
          valueFormatter: params => {
            const { aggData } = params.node;
            let formatValue = 0;
            if (aggData) {
              const { targetInvQty, currentInvQtyGap } = aggData;
              formatValue =  targetInvQty !== 0 ? (currentInvQtyGap / targetInvQty).toFixed(4) : 0;
            }
            return numberUtil.formatNumber(formatValue, { style: "percent", maximumFractionDigits: 2, minimumFractionDigits: 2 });
          },
          aggFunc: 'avg',
          enableValue: true,
        },
        {
          headerName: "onhandAmt",
          field: "currentInvAmt",
          width: 100,
          type: ["currencyNumberColumn", "nonEditableColumn", "rightAligned"],
          aggFunc: 'sum',
          enableValue: true,
        },
        {
          headerName: "amtGapFromTarget",
          field: "currentInvAmtGap",
          width: 132,
          type: ["currencyNumberColumn", "nonEditableColumn", "rightAligned"],
          columnGroupShow: "open",
          aggFunc: 'sum',
          enableValue: true,
        },
        {
          headerName: "amtGapPctFromTarget",
          field: "currentInvAmtGapRate",
          width: 132,
          type: ["currencyNumberColumn", "nonEditableColumn", "rightAligned"],
          columnGroupShow: "open",
          valueFormatter: params => {
            const { aggData } = params.node;
            let formatValue = 0;
            if (aggData) {
              const { targetInvAmt, currentInvAmtGap } = aggData;
              formatValue = targetInvAmt !== 0 ? (currentInvAmtGap / targetInvAmt).toFixed(4) : 0;
            }
            return numberUtil.formatNumber(formatValue, { style: "percent", maximumFractionDigits: 2, minimumFractionDigits: 2 });
          },
          aggFunc: 'avg',
          enableValue: true,
        },
        {
          headerName: "avgStockMonthly",
          field: "avgInvQty",
          width: 135,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          columnGroupShow: "open",
          aggFunc: 'sum',
          enableValue: true
        },
        {
          headerName: "avgStockMonthlyAmt",
          field: "avgInvAmt",
          width: 135,
          type: ["currencyNumberColumn", "nonEditableColumn", "rightAligned"],
          columnGroupShow: "open",
          aggFunc: 'sum',
          enableValue: true
        }
      ]
    },
    {
      headerName: "onhandSku",
      children: [
        {
          headerName: "excess",
          field: "currentExcess",
          width: 90,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          aggFunc: 'sum',
          enableValue: true,
        },
        {
          headerName: "shortage",
          field: "currentShortage",
          width: 100,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          aggFunc: 'sum',
          enableValue: true,
        },
        {
          headerName: "stockout",
          field: "currentExist",
          width: 100,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          aggFunc: 'sum',
          enableValue: true,
        }
      ]
    },
    {
      headerName: "riskSku",
      children: [
        {
          headerName: "excess",
          field: "riskExcess",
          width: 100,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          aggFunc: 'sum',
          enableValue: true,
        },
        {
          headerName: "stockout",
          field: "riskStockout",
          width: 100,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          aggFunc: 'sum',
          enableValue: true,
        },
        {
          headerName: "obsolescence",
          field: "riskObsolescence",
          width: 130,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          aggFunc: 'sum',
          enableValue: true,
        }
      ]
    },
    {
      headerName: "demandTrend",
      children: [
        {
          headerName: "rising",
          field: "trendRising",
          width: 90,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          aggFunc: 'sum',
          enableValue: true,
        },
        {
          headerName: "shrinking",
          field: "trendShirinking",
          width: 100,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
          aggFunc: 'sum',
          enableValue: true,
        }
      ]
    }
  ];

  const onAcceptHandler = (dateValue, type) => {
    if (isEqual(type, DEMAND)) {
        dateValue.setHours(0);
        dateValue.setMinutes(0);
        dateValue.setSeconds(0);
        dateValue.setDate(1);

        setDemandStartDate(dateValue);
        const demandEndDate = new ZDate(dateValue.getFullYear(), dateValue.getMonth() + Number(demandMonths), 0);

        setDemandEndDate(demandEndDate);
    } else {
        dateValue.setHours(23);
        dateValue.setMinutes(59);
        dateValue.setSeconds(59);
        dateValue.setMonth(dateValue.getMonth() + 1);
        dateValue.setDate(0);

        setActualEndDate(dateValue);
        const actualStartDate = new ZDate(dateValue.getFullYear(), dateValue.getMonth() - Number(actualMonths) + 1, 1);

        setActualStartDate(actualStartDate);
    }
  }

  const onGridReady = (params) => {
    setGridApi(params.api);

    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const autoGroupColumnDef = {
    editable: false,
    pinned: 'left',
    filterValueGetter(params) {
      const colGettingGrouped = params.colDef.showRowGroup;
      return params.api.getValue(colGettingGrouped, params.node);
    }
  }

  const pivotSetting = {
    pivotMode: true,
    suppressAggFuncInHeader: true,
    autoGroupColumnDef,
    sideBar: {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressSyncLayoutWithGrid: true,
            suppressColumnMove: true,
            suppressColumnSelectAll: true,
            suppressPivotMode: true,
            contractColumnSelection: true,
            suppressValues: true,
            suppressPivots: true
          }
        }
      ],
      position: "left"
    },
    // group(col1, col2) , col3 ... -> col1, col2, col3 option
    groupDisplayType: "multipleColumns",
  }

  const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#f5f5f9',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 230,
      fontSize: FS.SUB,
      border: '1px solid #dadde9',
    },
  }));

  useEffect(() => {
    if (gridApi) {
      DataState.initialize(gridApi);
      setEvaluationMonths();
      retrieveIpEvaluation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridApi]);

  useEffect(() => {
    if (!isEmpty(ispEvaluation)) {
      calculateTotal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ispEvaluation]);

  useEffect(() => {
    if (!isEmpty(categoryKeyValue)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryKeyValue])

  return (
    <>
      <FilterContainer>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <HtmlTooltip placement="right" title={
              <Typography component={"em"} sx={{ fontSize: FS.SUB }} color="inherit">{t("MSG_EvalMonths", { N: actualMonths })}</Typography>
            }>
              <Autocomplete
                size="small"
                sx={{ width: 110, mr: 1 }}
                value={actualMonths}
                onChange={(_, value) => onChangeEvalMonths(value, ACTUAL)}
                options={PERIOD_DATA_ARRAY}
                disableClearable
                renderInput={(params) => <TextField {...params} label={t("actualSales")} />}
              />
            </HtmlTooltip>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateField
                size="small"
                label={t("startDate")}
                format={"yyyy-MM"}
                value={actualMonths !== '0' ? actualStartDate : null}
                readOnly
                disabled
                sx={{ mr: 1, width: 120 }}
              />
              <DatePicker
                className="date-picker-small"
                label={t("endDate")}
                views={["year", "month"]}
                format={"yyyy-MM"}
                minDate={ACTUAL_MIN_DATE}
                maxDate={ACTUAL_MAX_DATE}
                value={actualMonths !== '0' ? actualEndDate : null}
                onAccept={(e) => onAcceptHandler(e, ACTUAL)}
                slotProps={{ textField: { InputProps: { color: 'primary', size: "small", sx: { mr: 1, width: 120 } } } }}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <HtmlTooltip placement="right" title={
              <Typography component={"em"} sx={{ fontSize: FS.SUB }} color="inherit">{t("MSG_EvalMonths", { N: demandMonths })}</Typography>
            }>
              <Autocomplete
                size="small"
                sx={{ width: 110, mr: 1 }}
                value={demandMonths}
                onChange={(_, value) => onChangeEvalMonths(value, DEMAND)}
                options={PERIOD_DATA_ARRAY}
                disableClearable
                renderInput={(params) => <TextField {...params} label={t("demandPlan")} />}
              />
            </HtmlTooltip>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                className="date-picker-small"
                label={t("startDate")}
                views={["year", "month"]}
                format={"yyyy-MM"}
                minDate={DEMAND_MIN_DATE}
                maxDate={DEMAND_MAX_DATE}
                value={demandMonths !== '0' ? demandStartDate : null}
                onAccept={(e) => onAcceptHandler(e, DEMAND)}
                slotProps={{ textField: { InputProps: { color: 'primary', size: "small", sx: { mr: 1, width: 120 } } } }}
              />
              <DateField
                size="small"
                label={t("endDate")}
                format={"yyyy-MM"}
                value={demandMonths !== '0' ? demandEndDate : null}
                readOnly
                disabled
                sx={{ mr: 1, width: 120 }}
              />
            </LocalizationProvider>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
          {canOpenEvalResult && <ResultButton onClick={showResultsPage} />}
          <RunButton label="runEvaluation" onClick={() => handleDialogHandler(RUN_EVALUATION)} />
          <CheckButton label="applyEvaluation" onClick={() => handleDialogHandler(APPLY_EVALUATION)} />
        </Box>
      </FilterContainer>
      <Box id="target-inventory-evaluation" className="ag-theme-balham grid-area ag-grid-box" sx={{ height: "calc(100% - 65px)" }}>
        <AgGridReact
          ref={gridRef}
          rowData={ispEvaluation}
          onGridReady={onGridReady}
          {...defaultGridMemo}
          {...pivotSetting}
          pinnedBottomRowData={[ispEvaluationTotal]}
        />
      </Box>
      <Box sx={{ height: "65px", mt: 1, mb: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
          {t("evaluatedStrategy")} : {deployedStrategy.name}
        </Typography>
        <Typography variant="body1">
          {t("strategyDeployed")} : {deployedStrategy.releaseTs ? format((new ZDate(deployedStrategy.releaseTs)), "yyyy.MM.dd") : ""}
        </Typography>
        <Typography variant="body1">
          {t("evaluationCreated")} : {!isEmpty(ispEvaluation) ? format((new ZDate(ispEvaluation[0].createdTs)), "yyyy.MM.dd") : ""}
        </Typography>
      </Box>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        actionName={dialogInfo.actionName}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
    </>
  );
};

export default withTranslation()(TargetInventoryEvaluation);